import React, { useState, useEffect, useCallback } from 'react';
import { useRAGStore } from '../store/ragStore';
import { CustomJsonData } from '../types/rag';
import { Save, RefreshCw, Edit3 } from 'lucide-react';

const CustomRagTab: React.FC = () => {
  const {
    currentContext,
    selectedChunkTool,
    selectedLanguage,
    uploadCustomJson,
    getCustomJson,
    refreshCurrentContext,
  } = useRAGStore();

  const [customText, setCustomText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const loadCustomJson = useCallback(async () => {
    if (!currentContext) return;
    
    setIsLoading(true);
    try {
      const data = await getCustomJson();
      if (data) {
        setCustomText(data.custom_text || '');
      }
    } catch (error) {
      console.error('Failed to load custom information:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentContext?.contextId]); // Remove getCustomJson from dependencies

  useEffect(() => {
    if (currentContext?.contextId) {
      loadCustomJson();
    }
  }, [currentContext?.contextId, loadCustomJson]);

  const handleSave = async () => {
    if (!currentContext || !customText.trim()) return;

    const jsonData: CustomJsonData = {
      custom_text: customText.trim(),
      updated_at: new Date().toISOString(),
    };

    try {
      await uploadCustomJson(jsonData);
      alert('Custom information saved successfully!');
    } catch (error) {
      alert('Failed to save custom information');
    }
  };



  if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Edit3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Context Selected
          </h3>
          <p className="text-gray-600">
            Please go to the Context Management tab to select a context before managing custom information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Custom Information</h3>
          <button
            onClick={() => refreshCurrentContext()}
            className="btn-secondary flex items-center space-x-2"
            title="Refresh context data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Content
            </label>
            <textarea
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Enter your custom information here..."
              className="input-field h-32 resize-none"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleSave}
              disabled={!customText.trim() || isLoading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              {isLoading ? 'Saving...' : 'Save to Context'}
            </button>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600">
            <strong>Current Context:</strong> {currentContext.contextId}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Chunk Tool:</strong> {selectedChunkTool.displayName}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Language:</strong> {selectedLanguage}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomRagTab;
